<template>
    <template v-if="!hasPlan">
        <p>
            <span class="me-2">Retrieving your plan...</span>
            <loading-spinner />
        </p>
    </template>
    <template v-else>
        <div class="table-responsive mb-3">
            <table class="table table-striped">
                <thead>
                    <tr>
                        <th scope="col" class="cell-header">Year</th>
                        <th scope="col" class="text-end cell-header">Take from pension pots</th>
                        <template v-if="showFullTable">
                            <th scope="col" class="text-end cell-header">Take from savings pots</th>
                            <th scope="col" class="text-end cell-header">
                                Everything else you expect to get
                                <Help title="Everything else" :text="HelpText.planTableEverythingElse()"></Help>
                            </th>
                            <th scope="col" class="text-end cell-header">
                                Less tax you expect to pay
                                <Help title="Tax to pay" :text="HelpText.planTableTaxToPay()"></Help>
                            </th>
                            <th scope="col" class="text-end cell-header">
                                Total you expect to get after tax
                                <Help title="Total" :text="HelpText.planTableTotal()"></Help>
                            </th>
                        </template>
                        <th scope="col" class="text-end cell-header">Monthly</th>
                    </tr>
                </thead>
                <tbody>
                    <template v-for="planYear in retirementPlanYears" v-bind:key="planYear.startYear">
                        <tr>
                            <td scope="row">
                                {{ startYearText(planYear.startYear) }}
                            </td>
                            <td class="text-end">
                                {{ formatter.formatCurrency(planYear.takeFromPensions) }}
                            </td>
                            <template v-if="showFullTable">
                                <td class="text-end">
                                    {{ formatter.formatCurrency(planYear.takeFromSavings) }}
                                </td>
                                <td class="text-end">
                                    {{ formatter.formatCurrency(planYear.everythingElseExpectToGet) }}
                                </td>
                                <td class="text-end">
                                    {{ formatter.formatCurrency(planYear.tax) }}
                                </td>
                                <td class="text-end">
                                    {{ formatter.formatCurrency(planYear.totalAfterTax) }}
                                </td>
                            </template>
                            <td class="text-end" @click="toggleShowMonthDetail(planYear)">
                                {{ planYear.displayMonthDetail ? "Hide" : "Show" }}
                            </td>
                        </tr>
                        <tr v-if="planYear.displayMonthDetail">
                            <td colspan="3">
                                <div>
                                    <table class="table mb-0">
                                        <thead>
                                            <tr>
                                                <th scope="col" class="">Month</th>
                                                <th scope="col" class="text-end">
                                                    Take from Pension Pots
                                                </th>
                                                <th scope="col" class="text-end">
                                                    Take from Savings Pots
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <template v-for="planMonth in planYear.planMonths"
                                                v-bind:key="`${planYear.startYear}-${planMonth.month}`">
                                                <tr>
                                                    <th scope="row">
                                                        {{ getMonthName(planMonth.month) }}
                                                    </th>
                                                    <td class="text-end">
                                                        {{
                                                            formatter.formatCurrency(
                                                                planMonth.takeFromPensions
                                                            )
                                                        }}
                                                    </td>
                                                    <td class="text-end">
                                                        {{
                                                            formatter.formatCurrency(
                                                                planMonth.takeFromSavings
                                                            )
                                                        }}
                                                    </td>
                                                </tr>
                                            </template>
                                        </tbody>
                                    </table>
                                </div>
                            </td>
                        </tr>
                    </template>
                </tbody>
            </table>
        </div>
    </template>
</template>

<script setup lang="ts">
import { ref, computed } from "vue";
import { formatter } from "@/shared/ts/formatter";
import { RetirementPlanResponse } from "@/guiide/ts/response/retirementPlanResponse";
import { RetirementPlanYear } from "@/guiide/ts/response/retirementPlanYear";
import { getMonthName } from "@/guiide/ts/utilities";
import { useStore } from "vuex";
import HelpText from "@/guiide/ts/helpText";
import LoadingSpinner from "@/shared/components/LoadingSpinner.vue";
import Help from "./Help.vue";

const props = defineProps({
    showFullTable: { type: Boolean, default: true },
});

const displayFullRetirementPlan = ref(false);
const abbreviatedPlanRowCount = 5;
const store = useStore();
const retirementPlan = computed<RetirementPlanResponse>(() => store.state.plan);

const hasPlan = computed<boolean>(() => {
    return retirementPlan.value != null;
});

const retirementPlanYears = computed<Array<RetirementPlanYear>>(() => {
    if (!hasPlan.value) {
        return new Array<RetirementPlanYear>();
    }

    const itemCount = displayFullRetirementPlan.value
        ? 100
        : abbreviatedPlanRowCount;

    return retirementPlan.value.years.slice(0, itemCount);
});

function startYearText(year: number): string {
    return `${year}/${year + 1 - 2000}`;
}

function toggleShowMonthDetail(retirementPlanYear: RetirementPlanYear) {
    retirementPlanYear.displayMonthDetail = !retirementPlanYear.displayMonthDetail;
}

function toggleFullPlanDisplay() {
    displayFullRetirementPlan.value = !displayFullRetirementPlan.value;
}

defineExpose({
    toggleFullPlanDisplay
});

</script>

<style scoped>
.cell-header {
    vertical-align: top;
}
</style>